/*
 * Copyright © 2020-2025 XCamera. All Rights Reserved.
 * @Description: AI识别模块统一埋点Hook
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-08-12 11:45:46
 */
import { useCallback, useRef, useEffect } from 'react'
import sensors from '../../../utils/sensors'
import { useAIRecognition } from '../context/AIRecognitionContext'
import { XC_RECOGNITION_STATUS } from '../types'
import CONFIG from '../../../constants/config'

/**
 * 埋点事件类型定义
 */
export interface XCSensorTrackingParams {
  clickItem: string
  picUrl?: string
  [key: string]: any
}

/**
 * AI识别模块统一埋点Hook
 * 提供统一的埋点上报功能，避免重复代码
 */
export const useSensorTracking = () => {
  const {
    userId,
    groupId,
    isGroupVIP,
    myRole,
    recognitionResult,
    selectedImages,
    recognitionStatus,
    rememberChoice
  } = useAIRecognition()

  // 使用 useRef 保存最新的状态值
  const latestStateRef = useRef({
    recognitionResult,
    selectedImages,
    recognitionStatus,
    rememberChoice
  })

  // 更新 ref 中的最新值
  useEffect(() => {
    latestStateRef.current = {
      recognitionResult,
      selectedImages,
      recognitionStatus,
      rememberChoice
    }
  }, [recognitionResult, selectedImages, recognitionStatus, rememberChoice])

  // 基础参数，用于所有埋点事件
  const baseParams = {
    // userID: userId,
    groupID: groupId,
    isGroupVIP: isGroupVIP,
    role: CONFIG.saKeywords.roles[myRole]
  }

  /**
   * 获取当前识别相关参数
   * @returns 识别相关参数对象
   */
  const getCurrentRecognitionParams = useCallback(() => {
    // 从 ref 获取最新的状态值
    const {
      recognitionResult: currentResult,
      selectedImages: currentImages,
      recognitionStatus: currentStatus
    } = latestStateRef.current

    // 动态计算识别相关参数
    return currentStatus === XC_RECOGNITION_STATUS.COMPLETED
      ? {
          string1: currentResult?.potentialHazardList?.join(';') || '',
          string2: currentResult?.standardList?.join(';') || '',
          string3: currentResult?.thinking || '',
          picUrl: currentImages?.[currentImages.length - 1] || undefined
        }
      : {}
  }, [])

  /**
   * web端AI识别安全隐患的过程和结果事件
   * @param params 埋点参数
   * - actionType: 操作类型 indentifyBegin:识别开始   indentifySucc:识别成功 indentifyFail:识别失败
   * - string1: '', // 识别到的安全隐患
   * - string2: '', // 参考标准
   * - string3: '', // 思考过程
   * - picUrl: '', // 图片URL，在识别成功状态时需要上报图片的URL
   * - errorMsg: '', // 错误信息
   * - taskID: '', // 任务ID
   * clickItem 可选值:
   */
  const reportAISafetyHazardsResultClick = useCallback(
    (params: XCSensorTrackingParams) => {
      // 从 ref 获取最新的状态值
      const {
        recognitionResult: currentResult,
        selectedImages: currentImages
      } = latestStateRef.current

      // 根据 actionType 决定是否上报识别结果参数
      // 识别开始时不上报 string1、string2、string3
      // 识别成功和失败时上报这些参数
      const buildRecognitionParams = () => {
        const baseRecognitionParams = {
          picUrl: currentImages?.[currentImages.length - 1] || undefined
        }

        const shouldIncludeResultData =
          params.actionType === 'indentifySucc' ||
          params.actionType === 'indentifyFail'

        if (shouldIncludeResultData) {
          return {
            ...baseRecognitionParams,
            string1: currentResult?.potentialHazardList?.join(';') || '',
            string2: currentResult?.standardList?.join(';') || '',
            string3: currentResult?.thinking || ''
          }
        }

        return baseRecognitionParams
      }

      const recognitionParams = buildRecognitionParams()

      sensors.track('web_get_action_AI_Identify_safety_hazards_result', {
        ...baseParams,
        ...recognitionParams,
        ...params
      })
    },
    [userId, groupId, isGroupVIP, myRole]
  )

  /**
   * 上报AI安全隐患识别页面点击事件
   * @param params 埋点参数
   * - string1: '', // 识别到的安全隐患
   * - string2: '', // 参考标准
   * - string3: '', // 思考过程
   * - picUrl: '', // 图片URL，在识别成功状态相关的事件需要上报图片的URL
   * clickItem 可选值:
   * - enterPage: 进入页面
   * - choosePhoto: 点击选择照片
   * - batchChoose: 点击批量选择
   * - chooseAgain: 点击重新上传照片
   * - clickOneExamplePhoto: 点击一张示例照片
   * - pageUp: 示例照片点击上一页
   * - pageDown: 示例照片点击下一页
   * - switchPic: 点击换一批示例照片
   * - identifyHistory: 点击识别记录
   * - feedback: 点击反馈
   * - exportWord: 导出分析结果
   * - editResult: 点击编辑识别到的隐患
   * - editStandard: 点击编辑参考标准
   * - deleteOneResult: 点击删除一个识别到的隐患
   */
  const reportAISafetyHazardsClick = useCallback(
    (params: XCSensorTrackingParams) => {
      const recognitionParams = getCurrentRecognitionParams()

      sensors.track('web_click_page_AI_Identify_safety_hazards', {
        ...baseParams,
        ...recognitionParams,
        ...params
      })
    },
    [userId, groupId, isGroupVIP, myRole, getCurrentRecognitionParams]
  )

  /**
   * 上报AI识别安全隐患记录页事件
   * @param params 埋点参数
   * - clickItem: 点击的元素
   * - historyRecordNum: 历史记录总条数
   * - isGroupVIP: 是否团队会员
   * - role: 操作人角色
   * - groupID: 团队ID
   * clickItem 可选值:
   * - enterPage: 进入页面
   * - downloadExcel: 下载表格Excel
   */
  const reportAISafetyHazardsHistoryClick = useCallback(
    (params: XCSensorTrackingParams) => {
      const recognitionParams = getCurrentRecognitionParams()

      sensors.track('web_click_page_AI_Identify_safety_hazards_history', {
        ...baseParams,
        ...recognitionParams,
        ...params
      })
    },
    [userId, groupId, isGroupVIP, myRole, getCurrentRecognitionParams]
  )

  /**
   * 上报AI安全隐患识别功能满意度反馈事件
   * @param params 埋点参数
   * - rating: 评分 (0-10)
   * - feedbackContent: 反馈内容
   * - taskID: 任务ID
   * - string1: 自定义字段1
   * - string2: 自定义字段2
   * - string3: 自定义字段3
   */
  const reportAISafetyHazardsFeedback = useCallback(
    (params: {
      score: number
      content: string
      willPhoneCallback?: boolean
    }) => {
      const recognitionParams = getCurrentRecognitionParams()

      sensors.track('web_feedback_AI_Identify_safety_hazards_satisfaction', {
        ...baseParams,
        ...recognitionParams,
        ...params,
        bool1: params.willPhoneCallback ?? false,
        bool2: latestStateRef.current.rememberChoice
      })
    },
    [userId, groupId, isGroupVIP, myRole]
  )

  return {
    reportAISafetyHazardsClick,
    reportAISafetyHazardsResultClick,
    reportAISafetyHazardsHistoryClick,
    reportAISafetyHazardsFeedback
  }
}

export default useSensorTracking
